import CommonService, { ICommonService } from "../../../../services/CommonService";
import { ICardService } from "./ICardService";
import { CardServiceDefaults } from "./CardServiceDefaults";
import { ICardDispatcherService } from "./ICardDispatcherService";
import { isMobile } from "../../../helpers/DeviceHelpers";
import { isDarkTheme } from 'components/helpers/ThemeHelpers';

class CardService implements ICardService {

    _commonService: ICommonService = new CommonService();
    _defaults = { ...CardServiceDefaults };

    _contentClassNames:string[] = [];

    _isDefaultSwipeMode = false;

    _withIndicator = false;
    _withHeader = false;
    _withHeaderActions = false;
    _withHover = false;
    _withPointer = false;
    _canSwipeLeft = false;
    _canSwipeRight = false;
    _canAccept = false;
    _canDelete = false;
    _hasActions = false;
    _canSelect = false;
    _hasOnToggleSelect = false;
    _disabled = false;
    _pinSelect = false;

    _id = 0;
    _containerWidth = 0;
    _contentWidth = 0;
    _actionsFullThreshold = 0;
    _leadingActionsWidth = 0;
    _trailingActionsWidth = 0;
    _isLongPress = false;
    _cancelOnTouchEndOrOnMouseUp = false;
    _leadingActionsOpened = false;
    _trailingActionsOpened = false;
    _wasTrailingActionsOpened = false;
    _previousSwipe: any = null;

    _dispatcherService: ICardDispatcherService | null = null;

    _onLeadingActionsWidthChange: (width: number) => void = () => { };
    _onLeadingActionsClassNameChange: (className: string) => void = () => { };
    _onLeadingActionsOpacityChange: (width: number) => void = () => { };

    _onTrailingActionsWidthChange: (width: number) => void = () => { };
    _onTrailingActionsClassNameChange: (className: string) => void = () => { };
    _onTrailingActionsOpacityChange: (width: number) => void = () => { };

    _onContentClassNamesChange: (classNames: string[]) => void = () => { };
    _onContentBorderWidthChange: (offset: number) => void = () => { };

    _onDeleteOpacityChange: (opacity: number) => void = () => { };
    _onDeleteDisplayChange: (display: string) => void = () => { };

    constructor(
        id: number,
        swipeMode: string,
        showIndicator: boolean,
        canAccept: boolean,
        canSelect: boolean,
        canDelete: boolean,
        hasActions: boolean,
        hasTitle: boolean,
        hasSubtitle: boolean,
        hasOnToggleSelect: boolean,
        hasOnClick: boolean,
        pinSelect: boolean,
    ) {  
        this._isDefaultSwipeMode = swipeMode === "default";

        this._canSwipeLeft = canDelete;
        this._canSwipeRight = (canSelect || canAccept);
        this._id = id;
        this._withIndicator = showIndicator;
        this._withHeader = hasTitle || hasSubtitle || hasActions || canDelete;
        this._withHeaderActions = hasActions || canDelete;
        this._withHover = hasOnClick;
        this._withPointer = hasOnClick || this._canSwipeLeft || this._canSwipeRight;
        this._canAccept = canAccept;
        this._canDelete = canDelete;
        this._hasActions = hasActions;
        this._canSelect = canSelect;
        this._hasOnToggleSelect = hasOnToggleSelect;
        this._pinSelect = pinSelect;
    }

    get containerWidth() {
        return this._containerWidth;
    }

    get isDefaultSwipeMode() {
        return this._isDefaultSwipeMode;
    }

    get canSwipe () {
        return (this._canSwipeLeft || this._canSwipeRight) && !this._isLongPress && !this._disabled;
    }

    get canDelete() {
        return this._canDelete;
    }

    get canAccept() {
        return this._canAccept;
    }

    get canSelect() {
        return this._canSelect;
    }

    get hasActions() {
        return this._hasActions;
    }

    get isLeadingActionsOpened() {
        return this._leadingActionsOpened;
    }

    readonly classNameCrdActionsReturn = "crd-actions-return";
    readonly classNameCrdReturnModeOffset = "crd-return-mode-offset";

    get classNameContainer() {
        return this._isDefaultSwipeMode ? "crd-container-mode-default" : "";
    }

    get classNameLeadingActions() {
        return this._isDefaultSwipeMode ? "crd-leading-actions-mode-default" : "crd-leading-actions-mode-offset";
    }

    get classNameTrailingActions() {
        return this._isDefaultSwipeMode ? "crd-trailing-actions-mode-default" : "crd-trailing-actions-mode-offset";
    }

    get classNameCrd() {
        let className = "";
        if (this._withIndicator) {
            className += " crd-with-indicator";
        }
        if (this._withHeader) {
            className += " crd-with-header";
        }
        if (this._withHeaderActions) {
            className += " crd-with-header-actions";
        }
        if (this._withHover && !isMobile()) {
            className += " crd-with-hover";
        }
        if (this._withPointer) {
            className += " crd-with-pointer";
        }
        if (this._isDefaultSwipeMode) {
            className += " crd-mode-default";
        }

        return className;
    }

    get cancelOnTouchEndOrOnMouseUp() {
        return this._cancelOnTouchEndOrOnMouseUp;
    }

    set dispatcherService(dispatcherService: ICardDispatcherService) {
        this._dispatcherService = dispatcherService;
    }

    set onLeadingActionsWidthChange(callback: (width: number) => void) {
        this._onLeadingActionsWidthChange = callback;
    }

    set onLeadingActionsClassNameChange(callback: (className: string) => void) {
        this._onLeadingActionsClassNameChange = callback;
    }

    set onLeadingActionsOpacityChange(callback: (width: number) => void) {
        this._onLeadingActionsOpacityChange = callback;
    }

    set onTrailingActionsWidthChange(callback: (width: number) => void) {
        this._onTrailingActionsWidthChange = callback;
    }

    set onTrailingActionsClassNameChange(callback: (className: string) => void) {
        this._onTrailingActionsClassNameChange = callback;
    }

    set onTrailingActionsOpacityChange(callback: (width: number) => void) {
        this._onTrailingActionsOpacityChange = callback;
    }

    set onContentClassNamesChange(callback: (classNames: string[]) => void) {
        this._onContentClassNamesChange = callback;
    }

    set onContentBorderWidthChange(callback: (offset: number) => void) {
        this._onContentBorderWidthChange = callback;
    }

    set onDeleteOpacityChange(callback: (opacity: number) => void) {
        this._onDeleteOpacityChange = callback;
    }

    set onDeleteDisplayChange(callback: (display: string) => void) {
        this._onDeleteDisplayChange = callback;
    }

    // Public
    setDisabled(disabled: boolean) {
        this._disabled = disabled;
    }

    setCanDelete(canDelete: boolean) {
        this._canDelete = canDelete;
        this._canSwipeLeft = canDelete;
    }

    setContainerWidth(width: number) {
        this._containerWidth = width;
        this._actionsFullThreshold = width / 2;
    }

    setContentWidth(width: number) {
        this._contentWidth = width;
    }

    setCanAccept(canAccept: boolean) {
        if (this._canAccept === canAccept) return;

        this._canAccept = canAccept;
        this._canSwipeRight = canAccept;
    }

    onToggleSelect(show: boolean) {
        this.prepareElements();

        this._leadingActionsWidth = show ? this._defaults.actionsThreshold : 0;
        this._trailingActionsWidth = 0;

        this.onSetWidths(true);
    }

    prepareElements() {
        this._onLeadingActionsClassNameChange(this.classNameCrdActionsReturn);
        this._onTrailingActionsClassNameChange(this.classNameCrdActionsReturn);

        this._contentClassNames = [];

        this._onContentClassNamesChange(this._contentClassNames);
    }

    onSwiped() {
        if (!this.canSwipe) return;

        this._onLeadingActionsClassNameChange(this.classNameCrdActionsReturn);
        this._onTrailingActionsClassNameChange(this.classNameCrdActionsReturn);

        if (!this._isDefaultSwipeMode) {
            this._contentClassNames.push(this.classNameCrdReturnModeOffset);
            this._onContentClassNamesChange(this._contentClassNames);
        }

        let shoulSetWidths = false;
        if (this._leadingActionsWidth > 0) {
            if (this._canAccept) {
                if (this._leadingActionsWidth >= this._actionsFullThreshold) {
                    this._leadingActionsWidth = this._containerWidth;
                } else {
                    this._leadingActionsWidth = 0;
                }
            } else if (this._canSelect) {
                if (this._leadingActionsWidth < this._defaults.actionsThreshold) {
                    this._leadingActionsWidth = 0;
                } else {
                    this._leadingActionsWidth = this._defaults.actionsThreshold;
                }
            }

            shoulSetWidths = true;
        }

        if (this._trailingActionsWidth > 0) {
            if (this._trailingActionsWidth < this._defaults.actionsThreshold) {
                this._trailingActionsWidth = 0;
            } else if (this._trailingActionsWidth >= this._actionsFullThreshold) {
                this._trailingActionsWidth = this._containerWidth;
            } else {
                this._trailingActionsWidth = this._defaults.actionsThreshold;
            }
            shoulSetWidths = true;
        }

        if (shoulSetWidths) {
            this.onSetWidths(true);
        }

        this._previousSwipe = null;

        if (!this._leadingActionsOpened && this._dispatcherService) {
            this._dispatcherService.onSelect(false);
        }
    }

    onSwipeStart() {
        this._cancelOnTouchEndOrOnMouseUp = true;
        this._wasTrailingActionsOpened = this._trailingActionsOpened;
    }

    onSwiping(e: any) {
        if (!this.canSwipe) return;

        if (e.dir !== "Left" && e.dir !== "Right") return;

        const { isSwipingLeft, distance } = this.getSwipingValues(e);

        if (isSwipingLeft) {
            this.onSwipingLeft(distance);
        } else {
            this.onSwipingRight(distance);
        }

        this.onSetWidths(false);

        this._previousSwipe = e;
    }

    onTouchStartOrOnMouseDown() {
        this._isLongPress = false;
        this._cancelOnTouchEndOrOnMouseUp = false;

        this._onLeadingActionsClassNameChange("");
        this._onTrailingActionsClassNameChange("");

        this._contentClassNames = this._contentClassNames.filter(
            (className) => className !== this.classNameCrdReturnModeOffset
        );
        this._onContentClassNamesChange(this._contentClassNames);
    }

    onLongPress() {
        this._isLongPress = true;
        this._cancelOnTouchEndOrOnMouseUp = true;

        if ((!this._canDelete && !this._canSelect)|| this._pinSelect) return;
        if (this._hasOnToggleSelect && this._canSelect) {
            if (this._dispatcherService) {
                this._dispatcherService.onToggleSelect();
                if (this._leadingActionsOpened) {
                    this._dispatcherService.onSelect(true);
                }
            }
        } else if (this._canSelect || this._canDelete) {
            this.prepareElements();
            if (this._leadingActionsOpened) {
                this._leadingActionsWidth = 0;
            } else if (this._trailingActionsOpened) {
                this._trailingActionsWidth = 0;
            } else {
                this._leadingActionsWidth = this._canSelect ? this._defaults.actionsThreshold : 0;
                this._trailingActionsWidth = this._canSelect ? 0 : this._defaults.actionsThreshold;
            }

            this.onSetWidths(true);
        }
    }

    onCloseDialog() {
        this._trailingActionsWidth = (this._wasTrailingActionsOpened || this._trailingActionsOpened) ? this._defaults.actionsThreshold : 0;
        this.onSetWidths(false);
    }

    onReset() {
        this._trailingActionsWidth = (this._wasTrailingActionsOpened || this._trailingActionsOpened) ? this._defaults.actionsThreshold : 0;
        this.onSetWidths(false);
    }

    getColor(baseColor: string) {
        if (this._commonService.isColorDark(baseColor)) {
            return "#ffffff";
        }
        return "#333333"
    }

    // Private

    getSwipingValues(e: any) {
        let isSwipingLeft = false;
        let distance = 0;

        if (!this._previousSwipe) {
            isSwipingLeft = e.dir === "Left";
        } else {
            const currentDeltaX = e.deltaX;
            const previousDeltaX = this._previousSwipe.deltaX;

            if (currentDeltaX < previousDeltaX) {
                isSwipingLeft = true;
                distance = previousDeltaX - currentDeltaX;
            }
            if (currentDeltaX > previousDeltaX) {
                distance = currentDeltaX - previousDeltaX;
            }
        }
        return { isSwipingLeft, distance: distance }
    }

    onSwipingLeft(distance: number) {
        if (this._leadingActionsWidth > 0) {
            if(this._pinSelect) return;
            this._leadingActionsWidth = this._leadingActionsWidth - distance;
            if (this._leadingActionsWidth < 0) {
                this._leadingActionsWidth = 0;
            }
        } else if (this._canSwipeLeft) {
            this._trailingActionsWidth = this._trailingActionsWidth + distance;
            if (!this._canSwipeRight && this._trailingActionsWidth < 0) {
                this._trailingActionsWidth = 0;
            }
        }
    }

    onSwipingRight(distance: number) {
        if(this._pinSelect) return;
        if (this._trailingActionsWidth > 0) {
            this._trailingActionsWidth = this._trailingActionsWidth - distance;
            if (this._trailingActionsWidth < 0) {
                this._trailingActionsWidth = 0;
            }
        } else if (this._canSwipeRight) {
            this._leadingActionsWidth = this._leadingActionsWidth + distance;
            if (!this._canSwipeLeft && this._leadingActionsWidth < 0) {
                this._leadingActionsWidth = 0;
            }
        }
    }

    onSetWidths(doCallback: boolean) {

        this._contentClassNames = [];

        this._onLeadingActionsWidthChange(this._leadingActionsWidth);
        this._onTrailingActionsWidthChange(this._trailingActionsWidth);

        if (this._trailingActionsWidth > 0) {
            if (this._trailingActionsWidth >= this._containerWidth) {
                this._leadingActionsWidth = -10;
                if (doCallback) {
                    setTimeout(() => {
                        if (this._dispatcherService) {
                            this._dispatcherService.onDelete(null);
                        }
                    }, 250);
                }
            }
        }

        if (this._leadingActionsWidth > 0) {
            if (this._leadingActionsWidth >= this._containerWidth) {
                this._trailingActionsWidth = -10;
                if (doCallback) {
                    setTimeout(() => {
                        if (this._dispatcherService) {
                            this._dispatcherService.onAccept(null);
                        }
                        this._leadingActionsWidth = 0;
                        this._trailingActionsWidth = 0;
                        this.onSetWidths(false);
                    }, 250);
                }
            }
        }

        const hasContent = this._leadingActionsWidth < this._containerWidth &&
            this._trailingActionsWidth < this._containerWidth;
        this._onContentBorderWidthChange(hasContent ? 1 : 0);

        this._leadingActionsOpened = this._leadingActionsWidth >= this._defaults.actionsThreshold;
        this._trailingActionsOpened = this._trailingActionsWidth >= this._defaults.actionsThreshold;

        this.setDeleteOpacity();

        let leadingOpacity = 1;
        let trailingOpacity = 1;

        if (this._leadingActionsWidth < this._defaults.actionsThreshold) {
            leadingOpacity = (((this._leadingActionsWidth * 100) / this._defaults.actionsThreshold) / 100);
        }

        if (this._trailingActionsWidth < this._defaults.actionsThreshold) {
            trailingOpacity = (((this._trailingActionsWidth * 100) / this._defaults.actionsThreshold) / 100);
        }

        if (this._canSwipeRight)
            this._onLeadingActionsOpacityChange(leadingOpacity);

        if (this._canSwipeLeft)
            this._onTrailingActionsOpacityChange(trailingOpacity);

        this._onContentClassNamesChange(this._contentClassNames);

        if (!doCallback || !this._dispatcherService) return;


        if (this._leadingActionsOpened) {
            this._dispatcherService.onActivateSelect();
        } else {
            this._dispatcherService.onDeactivateSelect();
        }
    }

    setDeleteOpacity() {
        if (!this._canDelete || this._hasActions) return;

        let opacity = 0;
        if (this._trailingActionsWidth < this._defaults.actionsThreshold) {
            opacity = 1 - (((this._trailingActionsWidth * 100) / this._defaults.actionsThreshold) / 100);
        }

        this._onDeleteOpacityChange(opacity);
        this._onDeleteDisplayChange(opacity <= 0 ? "none" : "flex");
    }

        setHasActions(actions: boolean) {
        this._hasActions = actions;
    }
}
export default CardService;
